import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { selectAll } from 'd3'

import { DashboardContext } from 'live/context/dashboard'

import SurgeSwabSchematic from './schematicTypes/surgeSwabSchematic'
import HoleCleaningSchematic from './schematicTypes/holecleaningSchematic'
import { lithologyFunction } from './utils/lithology'
import { zoomFunction } from './utils/zoom'
import _ from 'lodash'
import { Svg } from 'live-charts/components/Svg'

const StyledSVG = styled.svg<{
    theme: string
    heightOfOGMCartesian: boolean
    lithology: boolean
    paddingTop: number
    paddingBottom: number
}>`
    margin: auto;
    /* padding-top: 30px; */
    /* padding-bottom: 80px; */
    /* display: block; */
    /* width: 100%; */
    /* height: 100%; */
  ${props =>
      props.heightOfOGMCartesian &&
      `
  padding-top: 83px;
  padding-bottom: 120px;
  `}
  
//padding-top: ${props => props.paddingTop + 'px'};
// padding-bottom: ${props => props.paddingBottom + 'px'};

    .starting-range-line,
  .ending-range-line {
        stroke: var(--gray-1000);
    }

    .starting-range,
    .ending-range {
        fill: var(--gray-1000);
    }

    .well-sections {
        stroke: #c8ccd4;
    }

    .well-sections {
        stroke: #c8ccd4;

        &.well-sections--open-hole {
            stroke: #868695;
        }
    }

    .casing-shoe,
    .drill-string {
        fill: #252c38;
    }

    .casing {
        stroke: #252c38;
        fill: none;
        stroke-width: 1px;
    }

    .drill-string {
        stroke: #252c38;
        stroke-width: 5px;
    }

    .drill-string-margin {
        stroke: #252c38;
        stroke-width: 11px;
    }

    .without-drill-bit {
        stroke: #bbb;
        stroke-width: 9px;
    }

    .before-begin {
        stroke: #868695;
    }

    .main-path {
        stroke: #868695;
        stroke-width: 20px;
    }

    .svg-schematic {
        transform: ${props => (props.lithology ? 'translateX(28px)' : 'translateX(0px)')};
    }

    .conectors-to-box-white {
        stroke: var(--gray-800);
    }

    .conectors-to-box-blue {
        stroke: #37a3ef;
        stroke-dasharray: 2 2;
    }

    .reset-zoom {
        cursor: pointer;
        transform: translate(100px, 17px);
        display: none;

        rect {
            stroke: #cccccc;
            stroke-width: 1;
            fill: #f7f7f7;
        }

        text {
            color: #333333;
            font-weight: normal;
            fill: #333333;
        }
    }

    .lithology-chart {
        z-index: 1;
    }

    .brush {
        z-index: 10;
    }
`
const Container = styled.div<{
    theme: string
    heightOfOGMCartesian: boolean
    lithology: boolean
    paddingTop: number
    paddingBottom: number
}>`
    width: 100%;
    height: 100%;
    .starting-range-line,
    .ending-range-line {
        stroke: var(--gray-1000);
    }

    .starting-range,
    .ending-range {
        fill: var(--gray-1000);
    }

    .well-sections {
        stroke: #c8ccd4;
    }

    .casing-shoe,
    .drill-string {
        fill: #252c38;
    }

    .casing {
        stroke: #252c38;
        fill: none;
        stroke-width: 1px;
    }

    .drill-string {
        stroke: #252c38;
        stroke-width: 5px;
    }

    .drill-string-margin {
        stroke: #252c38;
        stroke-width: 11px;
    }

    .without-drill-bit {
        stroke: #bbb;
        stroke-width: 9px;
    }

    .before-begin {
        stroke: #868695;
    }

    .main-path {
        stroke: #868695;
        stroke-width: 20px;
    }

    .svg-schematic {
        transform: ${props => (props.lithology ? 'translateX(28px)' : 'translateX(0px)')};
    }

    .conectors-to-box-white {
        stroke: var(--gray-800);
    }

    .conectors-to-box-blue {
        stroke: #37a3ef;
        stroke-dasharray: 2 2;
    }

    .reset-zoom {
        cursor: pointer;
        transform: translate(100px, 17px);
        display: none;

        rect {
            stroke: #cccccc;
            stroke-width: 1;
            fill: #f7f7f7;
        }

        text {
            color: #333333;
            font-weight: normal;
            fill: #333333;
        }
    }

    .lithology-chart {
        z-index: 1;
    }

    .brush {
        z-index: 10;
    }
`

const resetZoomSchematic = (range, setUpdateRange) => {
    selectAll('.reset-zoom').style('display', 'unset')
    setUpdateRange(range)
}

export default function GenericSchematic(props: SchematicValues): JSX.Element {
    const context = useContext(DashboardContext)
    const [updatedRange, setUpdatedRange] = useState(props.range)
    const [svgWidth, setSvgWidth] = useState(145)
    const [drillBitPosition] = useState(538)
    const SVGRef = useRef<SVGSVGElement>(null)
    const chartRef = useRef(null)
    const lithologyRef = useRef(null)
    const [isZoomed, setIsZoomed] = useState(false)

    const receiveZoomEvent = e => {
        if (e?.detail && props.range.length > 0) {
            if (e.detail?.zoom?.length > 0) {
                setUpdatedRange(e.detail.zoom)
                setIsZoomed(true)
            } else {
                resetZoomSchematic(props.range, setUpdatedRange)
                setIsZoomed(false)
            }
        }
    }

    useEffect(() => {
        if (props.allowZoom) {
            document.addEventListener('zoomOGMW', receiveZoomEvent)

            return () => document.removeEventListener('zoomOGMW', receiveZoomEvent)
        }
    }, [props.allowZoom])

    useEffect(() => {
        if (props.lithology.length > 0 && chartRef.current) {
            lithologyFunction(props.lithology, updatedRange, lithologyRef.current)
        }
    }, [props.lithology, chartRef.current, updatedRange])

    useEffect(() => {
        props.width < 159
            ? setSvgWidth((props.sections.length > 2 ? 120: 100))
            : props.width < 210
            ? setSvgWidth(20 + 82 + 23 + (props.sections.length > 2 ? 20 : 0) + 16 * (props.sections.length-2))
            : setSvgWidth(20 + 82 + 23 + (props.sections.length > 2 ? 20 : 0) + 16 * (props.sections.length-2))
    }, [JSON.stringify(props.sections)])

    useEffect(() => {
        if (chartRef.current && props.allowZoom) {
            zoomFunction(chartRef.current, updatedRange, setUpdatedRange, props.range)
        }
    }, [chartRef.current, updatedRange, props.allowZoom])

    return (
        <>
            {/* <Svg */}
            <StyledSVG
                theme={context.theme}
                lithology={props.lithology.length > 0}
                ref={chartRef}
                heightOfOGMCartesian={props.heightOfOGMCartesian}
                paddingTop={props.paddingTop}
                paddingBottom={props.paddingBottom}
                width={props.width}
                height={props.height}
                viewBox={`0 0 100% 100%`}
                transform={`translate(0,${props.paddingTop == null ? '0' : props.paddingTop})`}
                // preserveAspectRatio="none"
                // preserveAspectRatio="xMidYMin meet"
                // preserveAspectRatio="xMidYMax meet"
                // style={{ border: '2px solid red' }}
                myRef={SVGRef}
            >
                <g transform={`translate(${Math.max(0,props.width<210 && props.lithology.length > 0 ? 0 : ((props.width - svgWidth) / 2)+(props.width<210 && props.sections.length < 6 ? 6 : 0))}, 0)`}>
                    <defs>
                        <marker id="blue-circle" viewBox="0 0 12 12" refX="6" refY="6" orient="auto">
                            <circle fill="#FFFFFF" cx="6" cy="6" r="6" />
                            <circle fill="#37A3EF" cx="6" cy="6" r="4" />
                        </marker>
                    </defs>
                    <g ref={lithologyRef} />

                    {props.schematicType === 'surgenswab' &&
                        <SurgeSwabSchematic
                            {...props}
                            theme={context.theme}
                            lithology={props.lithology.length > 0}
                        />}

                    {props.schematicType === 'holecleaning' && (
                        <HoleCleaningSchematic
                            {...props}
                            theme={context.theme}
                            drillBitPosition={drillBitPosition}
                            range={updatedRange}
                            originalRange={props.range}
                            isZoomed={isZoomed}
                            lithology={props.lithology.length > 0}
                            translated={Math.max(0,props.width<210 && props.lithology.length > 0 ? 0 : ((props.width - svgWidth) / 2)+(props.width<210 && props.sections.length < 6 ? 6 : 0))}
                        />
                    )}
                </g>
            </StyledSVG>
        </>
    )
}
